import { useQueryClient } from '@tanstack/react-query';
import { ApprovalCard } from 'src/shared/components/thread-store/cards/ApprovalCard';
import { CsvFileCard } from 'src/shared/components/thread-store/cards/CsvFileCard';
import { TextValueCard } from 'src/shared/components/thread-store/cards/TextValueCard';
import {
  ThreadStore,
  PUSH_TEXT_TO_OUTLOOK_APP,
  PUSH_CSV_FILE_TO_OUTLOOK_APP,
  APPROVAL,
  PushTextToOutlookApp,
  PushCsvFileToOutlookApp,
  Approval,
} from 'src/features/flows/types/threadStore.types';

interface ThreadStoreCardProps {
  threadStore: ThreadStore;
  /** Whether to show download buttons. Default: true */
  showDownload?: boolean;
  /** Whether to show expand buttons. Default: true */
  showExpand?: boolean;
  /** Callback when an approval action is performed */
  onApprovalResult?: (result: 'approved' | 'rejected') => void;
}

/**
 * Renders the appropriate card component based on thread store type.
 * Platform-agnostic - works in Outlook plugin, main frontend, and Chrome extension.
 */
export function ThreadStoreCard({
  threadStore,
  showDownload = true,
  showExpand = true,
  onApprovalResult,
}: ThreadStoreCardProps) {
  const queryClient = useQueryClient();

  const handleApprovalResult = (result: 'approved' | 'rejected') => {
    // Invalidate queries to refresh data
    queryClient.invalidateQueries({ queryKey: ['thread-stores'] });
    queryClient.invalidateQueries({ queryKey: ['approvals-count'] });
    onApprovalResult?.(result);
  };

  switch (threadStore.type) {
    case PUSH_TEXT_TO_OUTLOOK_APP:
      return <TextValueCard data={(threadStore as PushTextToOutlookApp).data} />;

    case PUSH_CSV_FILE_TO_OUTLOOK_APP:
      return (
        <CsvFileCard
          threadStore={threadStore as PushCsvFileToOutlookApp}
          showDownload={showDownload}
          showExpand={showExpand}
        />
      );

    case APPROVAL:
      const approvalStore = threadStore as Approval;
      return (
        <ApprovalCard
          flowId={approvalStore.flowId}
          approvalId={approvalStore.data.approvalId}
          approvalStatus={approvalStore.data.status}
          approvalProps={approvalStore.data.valuesToApprove}
          approvedDate={approvalStore.data.approvedDate}
          threadStore={threadStore}
          onResult={handleApprovalResult}
          showDownload={showDownload}
        />
      );

    default:
      console.warn(`Unknown thread store type: ${(threadStore as any).type}`);
      return null;
  }
}

export default ThreadStoreCard;
